import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, DollarSign, Mail, Phone, MapPin, MessageCircle, UserPlus, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { productAPI } from '@/lib/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productAPI.getProduct(id);
      setProduct(response.data);
    } catch (error) {
      toast.error('Product not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !product) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/marketplace')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <Card className="overflow-hidden">
              <div className="h-96 bg-slate-100 flex items-center justify-center">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Package className="w-32 h-32 text-slate-300" />
                )}
              </div>
            </Card>
          </div>

          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 mb-4">
              {product.name}
            </h1>

            <div className="flex items-baseline gap-2 mb-6">
              <DollarSign className="w-8 h-8 text-accent" />
              <span className="text-5xl font-chivo font-black text-accent">
                {product.price_usd}
              </span>
              <span className="text-xl text-slate-600">per {product.unit}</span>
            </div>

            {/* Seller Country Badge */}
            {product.seller_country && (
              <div className="flex items-center gap-2 mb-6 p-3 bg-ocean-deep/5 rounded-lg border border-ocean-deep/20">
                <MapPin className="w-5 h-5 text-ocean-deep" />
                <span className="text-slate-700">Seller from <strong className="text-ocean-deep">{product.seller_country}</strong></span>
              </div>
            )}

            <Card className="p-6 mb-6">
              <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-3">
                Product Details
              </h3>
              <div className="space-y-2 text-sm">
                <p><strong>Description:</strong> {product.description}</p>
                <p><strong>Stock:</strong> {product.stock_quantity} {product.unit}s available</p>
                <p><strong>Minimum Order:</strong> {product.minimum_order} {product.unit}(s)</p>
                {product.origin_location && (
                  <p><strong>Origin:</strong> {product.origin_location}</p>
                )}
              </div>
            </Card>

            {/* Only show seller contact if logged in AND contact info is available */}
            {isLoggedIn && product.seller_contact && (
              <Card className="p-6 bg-green-50 border-green-200 mb-6">
                <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-3">
                  Seller Contact
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{product.seller_contact.name}</p>
                  {product.seller_contact.company !== 'N/A' && (
                    <p>{product.seller_contact.company}</p>
                  )}
                  <p className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    {product.seller_contact.email}
                  </p>
                  <p className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    {product.seller_contact.phone}
                  </p>
                  {product.seller_contact.country !== 'Not provided' && (
                    <p className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {product.seller_contact.country}, {product.seller_contact.province}
                    </p>
                  )}
                </div>
              </Card>
            )}

            <Button 
              className="w-full" 
              size="lg"
              onClick={() => {
                if (!isLoggedIn) {
                  setShowRegisterPrompt(true);
                } else if (user.role === 'buyer') {
                  // Navigate to buyer dashboard to message seller
                  navigate('/buyer/dashboard');
                  toast.info('Start a quote request to message the seller');
                } else {
                  toast.info('Sellers cannot contact other sellers');
                }
              }}
              data-testid="contact-seller-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Contact Seller
            </Button>
          </div>
        </div>

        {/* Registration Prompt Modal */}
        <Dialog open={showRegisterPrompt} onOpenChange={setShowRegisterPrompt}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <UserPlus className="w-6 h-6 text-ocean-deep" />
                Join ServPac
              </DialogTitle>
              <DialogDescription className="text-left pt-4 space-y-3">
                <p className="text-slate-700">
                  To contact sellers and access our full marketplace features, please register as a <strong>buyer</strong>.
                </p>
                <div className="bg-ocean-deep/5 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2"><strong>As a registered buyer, you can:</strong></p>
                  <ul className="text-sm text-slate-600 space-y-1 list-disc pl-4">
                    <li>Contact sellers directly</li>
                    <li>Request freight quotes</li>
                    <li>Message and negotiate with sellers</li>
                    <li>Access exclusive Pacific Island products</li>
                  </ul>
                </div>
                <p className="text-sm text-slate-500">
                  Already have an account? Sign in to continue.
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRegisterPrompt(false);
                  navigate('/login');
                }}
                className="flex-1"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
              <Button 
                onClick={() => {
                  setShowRegisterPrompt(false);
                  navigate('/register?role=buyer');
                }}
                className="flex-1 bg-coral hover:bg-coral-dark"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Register as Buyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ProductDetail;
